import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import translate from '../../../../commonComponents/translate';
import SubHeader from '../../../../commonComponents/pageLayout/SubHeader';
import TopicBanner from '../../../../commonComponents/pageLayout/header/TopicBanner';
import Title from '../../../../commonComponents/Title';
import ContentContainer from '../../../../commonComponents/pageLayout/ContentContainer';
import Loader from '../../../../services/loader';
import Notifications from '../../../../services/notifications';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { getStartDateAndEndDateInterval } from '../../../../services/dateUtilityFunctions';
import { useModal } from '../../../../services/customHooks';
import CareerFilters from './CareerFilters';
import ExpirationRiskAlerts from './overview/ExpirationRiskAlerts';
import OverviewStats from './overview/OverviewStats';
import VADistribution from './overview/VADistribution';
import NetworkStats from './overview/NetworkStats';
import VAEvolution from './overview/VAEvolution';
import ActiveSubscriptions from './overview/ActiveSubscriptions';
import LevelObjectives from './progression/LevelObjectives';
import TrophiesSection from './progression/TrophiesSection';
import CommissionSimulator from './simulator/CommissionSimulator';
import VAPerTier from './simulator/VAPerTier';
import BonusListData from './BonusListData';
import BonusListDataItemBlockModal from './modals/BonusListDataItemBlockModal';

const TABS = {
  OVERVIEW: 'overview',
  SIMULATOR: 'simulator',
  PROGRESSION: 'progression',
  REWARDS: 'rewards',
};

const TAB_LIST = [
  { key: TABS.OVERVIEW, label: 'pureliink.career.tab.overview' },
  { key: TABS.SIMULATOR, label: 'pureliink.career.tab.simulator' },
  { key: TABS.PROGRESSION, label: 'pureliink.career.tab.progression' },
  { key: TABS.REWARDS, label: 'pureliink.career.tab.rewards' },
];

function MyCareerPage(props) {
  const { t, _csrf } = props;

  const { loggedUser } = useContext(LoggedUserContext);
  const { organization } = useContext(ConfigsContext);
  const { directSelling } = organization;

  // Initial range in the plan timezone (BRAVON-6325), matching the
  // date-picker presets.
  const [initialStartDate, initialEndDate] =
    getStartDateAndEndDateInterval(directSelling);

  const loader = useRef(new Loader('#pureliink-my-career'));
  const [isLoading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);

  const [listDetailModal, setListDetailModal] = useModal({
    show: false,
    data: null,
  });

  const [selectedUser, setSelectedUser] = useState(loggedUser._id);

  const [filters, setFilters] = useState({
    dateStart: initialStartDate.valueOf(),
    dateEnd: initialEndDate.valueOf(),
  });

  // Overview state
  const [expirationRisks, setExpirationRisks] = useState([]);
  const [overviewData, setOverviewData] = useState(null);
  const [vaDistribution, setVADistribution] = useState([]);
  const [networkStats, setNetworkStats] = useState(null);
  const [evolutionData, setEvolutionData] = useState([]);
  const [activeSubscriptionsData, setActiveSubscriptionsData] = useState([]);

  // Progression state
  const [levelsData, setLevelsData] = useState([]);
  const [trophiesData, setTrophiesData] = useState([]);

  // Rewards state
  const [bonusListData, setBonusListData] = useState([]);

  const apiHeaders = useMemo(
    () => ({
      'Accept-Version': '1.0.0',
      'X-Gamification-API': 'Games',
      'X-CSRF-Token': _csrf,
      _csrf,
    }),
    [_csrf],
  );

  const userId = selectedUser || loggedUser._id;

  const fetchOverviewData = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    const params = { dateStart: filters.dateStart, dateEnd: filters.dateEnd };
    Promise.all([
      axios.get(
        `/api/tailor-made/pureliink/career/${userId}/expiration-risks`,
        { headers: apiHeaders },
      ),
      axios.get(`/api/tailor-made/pureliink/career/${userId}/overview`, {
        params,
        headers: apiHeaders,
      }),
      axios.get(`/api/tailor-made/pureliink/career/${userId}/va-distribution`, {
        params,
        headers: apiHeaders,
      }),
      axios.get(`/api/tailor-made/pureliink/career/${userId}/network-stats`, {
        params,
        headers: apiHeaders,
      }),
      axios.get(`/api/tailor-made/pureliink/career/${userId}/evolution`, {
        params,
        headers: apiHeaders,
      }),
      axios.get(
        `/api/tailor-made/pureliink/career/${userId}/active-subscriptions`,
        {
          params,
          headers: apiHeaders,
        },
      ),
    ])
      .then(([risks, overview, dist, network, evolution, activeSubs]) => {
        setExpirationRisks(risks.data.data || []);
        setOverviewData(overview.data.data);
        setVADistribution(dist.data.data || []);
        setNetworkStats(network.data.data);
        setEvolutionData(evolution.data.data || []);
        setActiveSubscriptionsData(activeSubs.data.data || []);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        Notifications.showErrorFromRequest(err, 'error_fetchCareerData');
      });
  }, [filters, userId, apiHeaders]);

  const fetchProgressionData = useCallback(() => {
    if (!userId) return;
    Promise.all([
      axios.get(`/api/tailor-made/pureliink/career/${userId}/levels`, {
        headers: apiHeaders,
      }),
      axios.get(`/api/tailor-made/pureliink/career/${userId}/trophies`, {
        headers: apiHeaders,
      }),
    ])
      .then(([levels, trophies]) => {
        setLevelsData(levels.data.data || []);
        setTrophiesData(trophies.data.data || []);
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchProgressionData');
      });
  }, [userId, apiHeaders]);

  const fetchBonusList = useCallback(() => {
    if (!userId) return;
    axios
      .get(`/api/tailor-made/pureliink/report/${userId}/bonus/list`, {
        headers: apiHeaders,
      })
      .then(({ data: response }) => {
        setBonusListData(response.data);
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchBonusListData');
      });
  }, [userId, apiHeaders]);

  useEffect(() => {
    if (loader.current) {
      if (isLoading) loader.current.show();
      else loader.current.hide();
    }
  }, [isLoading]);

  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData]);

  useEffect(() => {
    fetchProgressionData();
    fetchBonusList();
  }, [fetchProgressionData, fetchBonusList]);

  const handleDateChange = (dateStart, dateEnd) => {
    setFilters(prev => ({ ...prev, dateStart, dateEnd }));
  };

  const handleUserChange = value => {
    setSelectedUser(value || loggedUser._id);
  };

  const handleClickSeeAll = listDataItem => {
    setListDetailModal({
      show: true,
      data: { type: listDataItem.type, sponsorId: userId },
    });
  };

  return (
    <>
      {listDetailModal.show && (
        <BonusListDataItemBlockModal
          t={t}
          data={listDetailModal.data}
          handleCloseModal={() =>
            setListDetailModal({ show: false, data: null })
          }
        />
      )}
      <Title title={t('header_pureliinkMyCareer')}>
        <ContentContainer>
          <SubHeader title={t('header_pureliinkMyCareer')} />
          <TopicBanner />
          <div className="m-content" id="pureliink-my-career">
            <div className="container">
              <CareerFilters
                t={t}
                filters={filters}
                selectedUser={selectedUser}
                onDateChange={handleDateChange}
                onUserChange={handleUserChange}
              />

              <ul
                className="nav nav-tabs nav-tabs-line m-tabs-line m-tabs-line--2x m-tabs-line--brand"
                role="tablist"
              >
                {TAB_LIST.map(({ key, label }) => (
                  <li key={key} className="nav-item m-tabs__item">
                    <a
                      className={`nav-link m-tabs__link ${
                        activeTab === key ? 'active' : ''
                      }`}
                      onClick={() => setActiveTab(key)}
                      role="tab"
                      style={{ cursor: 'pointer' }}
                    >
                      {t(label)}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="tab-content mt-3">
                {activeTab === TABS.OVERVIEW && (
                  <div className="tab-pane active">
                    <ExpirationRiskAlerts t={t} data={expirationRisks} />
                    <OverviewStats
                      t={t}
                      data={overviewData}
                      dateStart={filters.dateStart}
                    />
                    <VADistribution t={t} data={vaDistribution} />
                    <NetworkStats t={t} data={networkStats} />
                    <ActiveSubscriptions
                      t={t}
                      data={activeSubscriptionsData}
                      filters={filters}
                    />
                    <VAEvolution t={t} data={evolutionData} />
                    <VAPerTier
                      t={t}
                      userId={userId}
                      apiHeaders={apiHeaders}
                      dateStart={filters.dateStart}
                      dateEnd={filters.dateEnd}
                    />
                  </div>
                )}
                {activeTab === TABS.SIMULATOR && (
                  <div className="tab-pane active">
                    <CommissionSimulator
                      t={t}
                      userId={userId}
                      apiHeaders={apiHeaders}
                    />
                  </div>
                )}
                {activeTab === TABS.PROGRESSION && (
                  <div className="tab-pane active">
                    <LevelObjectives t={t} data={levelsData} />
                    <TrophiesSection t={t} data={trophiesData} />
                  </div>
                )}
                {activeTab === TABS.REWARDS && (
                  <div className="tab-pane active">
                    <BonusListData
                      t={t}
                      listData={bonusListData}
                      handleClickSeeAll={handleClickSeeAll}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

MyCareerPage.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default translate(MyCareerPage);
